<template>
    <Dialog v-model:open="opened">
        <DialogTrigger>
            <slot />
        </DialogTrigger>
        <DialogContent>
            <DialogHeader class="items-center">
                <template #icon> <ICloudCalendarLogo /> </template>
                <DialogTitle>{{
                    integration
                        ? $t("integrations.icloud_calendar.modal.title_edit")
                        : $t("integrations.icloud_calendar.modal.title_add")
                }}</DialogTitle>
            </DialogHeader>
            <div class="px-6 sm:flex sm:items-start">
                <div class="mt-3 sm:mt-0 sm:text-left w-full text-center">
                    <h3
                        id="modal-title"
                        class="text-lg font-medium leading-6 text-iron-900"
                    ></h3>
                    <div class="mt-2">
                        <div v-if="hasError" class="w-full">
                            <Alert variant="error">
                                <span class="font-medium tracking-[-0.02em]">
                                    {{
                                        $t(
                                            "integrations.icloud_calendar.permissions.title",
                                        )
                                    }}
                                </span>

                                <span
                                    class="mt-2 text-sm block tracking-[-0.01em]"
                                >
                                    {{
                                        $t(
                                            "integrations.icloud_calendar.permissions.description",
                                        )
                                    }}
                                </span>
                            </Alert>
                        </div>

                        <div class="mt-4 gap-1.5 grid w-full items-center">
                            <Label for="icloud-mail">{{
                                $t(
                                    "integrations.icloud_calendar.modal.icloud_email_label",
                                )
                            }}</Label>
                            <div class="relative items-center">
                                <Input
                                    v-model="iCloudEmail"
                                    type="email"
                                    id="icloud-mail"
                                    class="pl-10"
                                />
                                <span
                                    class="inset-y-0 start-0 px-3 absolute flex items-center justify-center"
                                >
                                    <Mail class="size-6 text-iron-500" />
                                </span>
                            </div>
                        </div>

                        <div class="mt-4 gap-1.5 grid w-full items-center">
                            <Label for="appspecific-pass">{{
                                $t(
                                    "integrations.icloud_calendar.modal.app_specific_password_label",
                                )
                            }}</Label>

                            <div class="relative items-center">
                                <Input
                                    v-model="appSpecificPassword"
                                    type="text"
                                    id="appspecific-pass"
                                    class="pl-10"
                                />
                                <span
                                    class="inset-y-0 start-0 px-3 absolute flex items-center justify-center"
                                >
                                    <Lock class="size-6 text-iron-500" />
                                </span>
                            </div>

                            <p class="text-sm text-iron-500 tracking-[-0.01em]">
                                {{
                                    $t(
                                        "integrations.icloud_calendar.specific_pass.more_info",
                                    )
                                }}
                                <a
                                    :href="appleHelpLocalised"
                                    class="font-medium text-z-primary-600 hover:text-z-primary-700"
                                    target="_blank"
                                >
                                    {{
                                        $t(
                                            "integrations.icloud_calendar.specific_pass.apple_help",
                                        )
                                    }}.
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <DialogFooter>
                <DialogClose>
                    <Button variant="outline" type="button">{{
                        $t("cancel")
                    }}</Button>
                </DialogClose>

                <Button
                    class="ml-4"
                    type="button"
                    @click="createAppleCalendarIntegration"
                    >{{ integration ? $t("save_changes") : $t("add") }}</Button
                >
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { router } from "@inertiajs/vue3";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog-v2";
import route from "ziggy-js";
import ICloudCalendarLogo from "../logos/simple/ICloudCalendar.vue";
import { Lock, Mail } from "lucide-vue-next";
import { useI18n } from "vue-i18n";
import { Alert } from "@/components/ui/alert";

const props = defineProps<{
    preserveState?: boolean;
    integration?: any;
}>();

const hasError = computed(() => {
    return !!props.integration?.errors?.invalid_credentials;
});

const { locale } = useI18n();

const opened = ref(false);

const appleHelpLocalised = computed(() => {
    if (locale.value === "cs") {
        return "https://support.apple.com/cs-cz/102654";
    } else {
        return "https://support.apple.com/en-us/102654";
    }
});

const iCloudEmail = ref(props.integration?.data?.iCloudEmail || "");
const appSpecificPassword = ref(
    props.integration?.data?.appSpecificPassword || "",
);

console.log(props.integration);

const createAppleCalendarIntegration = function () {
    if (props.integration) {
        router.put(
            route(
                "app.i-cloud-calendar-integrations.update",
                props.integration.id,
            ),
            {
                iCloudEmail: iCloudEmail.value,
                appSpecificPassword: appSpecificPassword.value,
            },
            {
                preserveState: props.preserveState ?? false,
                onFinish: () => {
                    closeModal();
                },
            },
        );

        return;
    }

    router.post(
        "/icloud-calendar-integrations",
        {
            iCloudEmail: iCloudEmail.value,
            appSpecificPassword: appSpecificPassword.value,
        },
        {
            preserveState: props.preserveState ?? false,
            onFinish: () => {
                closeModal();
            },
            onSuccess: () => {},
            onError: (errors) => {
                console.log("on error", errors);
            },
        },
    );
};

const closeModal = () => {
    opened.value = false;
};
</script>

<style></style>
